import React, { useEffect, useState } from "react";
import { Form, Spin, Radio, Space, Divider, Switch, Input } from "antd";
import { ReactSVG } from "react-svg";
import { CopyToClipboard } from "react-copy-to-clipboard";
import PrimaryButton from "../../Buttons/PrimaryButton";
import { getTranslation } from "../../../utils/helper";

const NotificationSettingsForm = ({ isFetching, setting, handleSubmit }) => {
  const [form] = Form.useForm();

  const [copied, setCopied] = useState(false);
  const [isNotificationOnDispatch, setIsNotificationOnDispatch] = useState(false);
  const [isNotificationOnGoing, setIsNotificationOnGoing] = useState(false);
  const [isNotificationOnOrderCompletion, setIsNotificationOnOrderCompletion] = useState(false);
  const [notificationOnDispatchMsg, setNotificationOnDispatchMsg] = useState("");
  const [notificationOnGoingMsg, setNotificationOnGoingMsg] = useState("");
  const [notificationOnOrderCompletionMsg, setNotificationOnOrderCompletionMsg] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setCopied(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  useEffect(() => {
    if (setting) {
      form.setFieldsValue({
        ...setting,
      });
      setIsNotificationOnDispatch(setting.isNotificationOnDispatch);
      setIsNotificationOnGoing(setting.isNotificationOnGoing);
      setIsNotificationOnOrderCompletion(setting.isNotificationOnOrderCompletion);
      setNotificationOnDispatchMsg(setting.notificationOnDispatchMsg);
      setNotificationOnGoingMsg(setting.notificationOnGoingMsg);
      setNotificationOnOrderCompletionMsg(setting.notificationOnOrderCompletionMsg);
    }
  }, [setting]);

  const handleOnFinish = async values => {
    const params = { ...values };
    params.isNotificationOnDispatch = isNotificationOnDispatch;
    params.isNotificationOnGoing = isNotificationOnGoing;
    params.isNotificationOnOrderCompletion = isNotificationOnOrderCompletion;
    handleSubmit(params);
  };

  const previewMessage = message => {
    let preview = message;
    setting?.notificationFields &&
      Object.keys(setting?.notificationFields).forEach(field => {
        preview = preview?.replaceAll(`{${field}}`, setting.notificationFields[field]);
      });
    return preview;
  };

  return (
    <Spin spinning={isFetching}>
      <div className="SettingsForm NotificationSettingsForm">
        <Form form={form} scrollToFirstError layout="vertical" hideRequiredMark colon={false} onFinish={handleOnFinish}>
          <div className="FormContent">
            <Form.Item
              label={getTranslation(setting, "Settings", "How will your customers receive notifications?")}
              name="notificationBy"
              rules={[{ required: true, message: "Required" }]}>
              <Radio.Group>
                <Space direction="vertical">
                  <Radio value="SMS">{getTranslation(setting, "Settings", "Text Message")}</Radio>
                  <Radio value="EMAIL">{getTranslation(setting, "Settings", "Email")}</Radio>
                  <Radio value="SMS_AND_EMAIL">{getTranslation(setting, "Settings", "Text Message & Email")}</Radio>
                </Space>
              </Radio.Group>
            </Form.Item>
            <Divider />
            <label>{getTranslation(setting, "Settings", "What does the notification message say?")}</label>
            <div className="CustomFields">
              <div className="Title">
                {getTranslation(setting, "Settings", "These notification fields are available")}{" "}
                <i>({getTranslation(setting, "Settings", "Click to copy")})</i>{" "}
                {copied ? <span className="copied"> {getTranslation(setting, "Settings", "Copied")} </span> : null}
              </div>
              <div className="Fields">
                {setting?.notificationFields &&
                  Object.keys(setting?.notificationFields).map(field => (
                    <CopyToClipboard key={field} text={`{${field}}`} onCopy={() => setCopied(true)}>
                      <div className="Filed">{field}</div>
                    </CopyToClipboard>
                  ))}
              </div>
            </div>
            <div className="NotificationMessage">
              <div className="Item">
                <Form.Item>
                  <Switch
                    checked={isNotificationOnDispatch}
                    onChange={checked => setIsNotificationOnDispatch(checked)}
                  />{" "}
                  {getTranslation(setting, "Settings", "Dispatch Notification")}
                </Form.Item>
                <div className="Desc">{getTranslation(setting, "Settings", "Dispatch Notification Desc")}</div>
                <Form.Item name="notificationOnDispatchMsg" rules={[{ required: true, message: "Required" }]}>
                  <Input.TextArea rows={4} onChange={e => setNotificationOnDispatchMsg(e.target.value)} />
                </Form.Item>
                <div className="Desc Preview">{getTranslation(setting, "Settings", "Preview on mobile")}:</div>
                <div className="PreviewContent">{previewMessage(notificationOnDispatchMsg)}</div>
              </div>
              <Divider type="vertical" />
              <div className="Item">
                <Form.Item>
                  <Switch checked={isNotificationOnGoing} onChange={checked => setIsNotificationOnGoing(checked)} />{" "}
                  {getTranslation(setting, "Settings", "Going Next")}
                </Form.Item>
                <div className="Desc">{getTranslation(setting, "Settings", "Going Next Desc")}</div>
                <Form.Item name="notificationOnGoingMsg" rules={[{ required: true, message: "Required" }]}>
                  <Input.TextArea rows={4} onChange={e => setNotificationOnGoingMsg(e.target.value)} />
                </Form.Item>
                <div className="Desc Preview">{getTranslation(setting, "Settings", "Preview on mobile")}:</div>
                <div className="PreviewContent">{previewMessage(notificationOnGoingMsg)}</div>
              </div>
              <Divider type="vertical" />
              <div className="Item">
                <Form.Item>
                  <Switch
                    checked={isNotificationOnOrderCompletion}
                    onChange={checked => setIsNotificationOnOrderCompletion(checked)}
                  />{" "}
                  {getTranslation(setting, "Settings", "Order Completion")}
                </Form.Item>
                <div className="Desc">{getTranslation(setting, "Settings", "Order Completion Desc")}</div>
                <Form.Item name="notificationOnOrderCompletionMsg" rules={[{ required: true, message: "Required" }]}>
                  <Input.TextArea rows={4} onChange={e => setNotificationOnOrderCompletionMsg(e.target.value)} />
                </Form.Item>
                <div className="Desc Preview">{getTranslation(setting, "Settings", "Preview on mobile")}:</div>
                <div className="PreviewContent">
                  {previewMessage(notificationOnOrderCompletionMsg)}
                  <div className="Icon"></div>
                </div>
              </div>
            </div>
          </div>
          <Divider />
          <div className="FormFooter">
            <div className="Left"></div>
            <div className="Right">
              <PrimaryButton
                label={getTranslation(setting, "Common", "Update")}
                htmlType="submit"
                icon={<ReactSVG className="icon" src="/icons/submit.svg" />}
              />
            </div>
          </div>
        </Form>
      </div>
    </Spin>
  );
};

export default NotificationSettingsForm;
